import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { HelpCircle, X } from 'lucide-react';
import { CAPSULE_MANUAL_SECTIONS } from '@/lib/capsuleConfigHelpContent';

function renderBlock(block, key) {
  switch (block.type) {
    case 'h':
      return <h3 key={key} className="font-bold text-[#09090B] mt-4 mb-1.5 text-sm first:mt-0">{block.text}</h3>;
    case 'p':
      return <p key={key} className="mb-2 text-xs leading-relaxed">{block.text}</p>;
    case 'ul':
      return (
        <ul key={key} className="list-disc ml-4 mb-2 space-y-0.5 text-xs">
          {block.items.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      );
    case 'ol':
      return (
        <ol key={key} className="list-decimal ml-4 mb-2 space-y-0.5 text-xs">
          {block.items.map((item, i) => <li key={i}>{item}</li>)}
        </ol>
      );
    case 'code':
      return (
        <pre key={key} className="mb-3 p-3 bg-zinc-900 text-zinc-100 rounded-sm text-[11px] font-mono overflow-x-auto whitespace-pre-wrap">
          {block.text}
        </pre>
      );
    case 'note':
      return (
        <div key={key} className="mb-3 p-3 bg-amber-50 border border-amber-200/80 rounded-sm text-xs text-amber-900">
          {block.text}
        </div>
      );
    default:
      return null;
  }
}

export default function CapsuleConfigHelp({ initialSection = 'intro' }) {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(initialSection);

  useEffect(() => {
    if (open) setActiveId(initialSection);
  }, [open, initialSection]);

  const section = CAPSULE_MANUAL_SECTIONS.find((s) => s.id === activeId) || CAPSULE_MANUAL_SECTIONS[0];

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="help-button-capsule"
        className="rounded-sm text-xs h-8 text-brand-primary border-brand-primary/30 hover:bg-blue-50"
      >
        <HelpCircle className="w-3.5 h-3.5 mr-1" /> Ayuda
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white rounded-sm border border-brand-border shadow-xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="help-modal-capsule"
          >
            <div className="flex items-center justify-between p-4 border-b border-brand-border bg-zinc-50 shrink-0">
              <h2 className="text-sm font-bold uppercase tracking-[0.1em] text-[#09090B] flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-brand-primary" /> Manual de Configuración de Cápsula
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)} className="h-7 w-7 p-0">
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex flex-1 min-h-0">
              <nav className="w-52 shrink-0 border-r border-brand-border bg-zinc-50/80 overflow-y-auto p-2">
                {CAPSULE_MANUAL_SECTIONS.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setActiveId(s.id)}
                    data-testid={`capsule-manual-nav-${s.id}`}
                    className={`w-full text-left px-2.5 py-2 mb-0.5 rounded-sm text-[11px] leading-snug transition-colors ${
                      activeId === s.id
                        ? 'bg-white text-brand-primary font-semibold border border-brand-primary/20 shadow-sm'
                        : 'text-zinc-600 hover:bg-white/80 hover:text-zinc-900'
                    }`}
                  >
                    {s.title}
                  </button>
                ))}
              </nav>

              <div className="flex-1 overflow-y-auto p-5 text-sm text-zinc-700">
                <h3 className="text-base font-bold text-[#09090B] mb-3">{section.title}</h3>
                {section.blocks.map((block, i) => renderBlock(block, i))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Abre el manual en una sección concreta (p. ej. desde InlineHelp). */
export function CapsuleConfigHelpLink({ section = 'intro', children }) {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(section);

  useEffect(() => {
    if (open) setActiveId(section);
  }, [open, section]);

  const activeSection = CAPSULE_MANUAL_SECTIONS.find((s) => s.id === activeId) || CAPSULE_MANUAL_SECTIONS[0];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-brand-primary hover:text-[#001F6D] underline underline-offset-2 font-medium"
        data-testid={`capsule-manual-link-${section}`}
      >
        {children}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white rounded-sm border border-brand-border shadow-xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-brand-border bg-zinc-50 shrink-0">
              <h2 className="text-sm font-bold uppercase tracking-[0.1em] text-[#09090B] flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-brand-primary" /> Manual de Configuración de Cápsula
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)} className="h-7 w-7 p-0">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-1 min-h-0">
              <nav className="w-52 shrink-0 border-r border-brand-border bg-zinc-50/80 overflow-y-auto p-2">
                {CAPSULE_MANUAL_SECTIONS.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setActiveId(s.id)}
                    className={`w-full text-left px-2.5 py-2 mb-0.5 rounded-sm text-[11px] leading-snug transition-colors ${
                      activeId === s.id
                        ? 'bg-white text-brand-primary font-semibold border border-brand-primary/20 shadow-sm'
                        : 'text-zinc-600 hover:bg-white/80 hover:text-zinc-900'
                    }`}
                  >
                    {s.title}
                  </button>
                ))}
              </nav>
              <div className="flex-1 overflow-y-auto p-5 text-sm text-zinc-700">
                <h3 className="text-base font-bold text-[#09090B] mb-3">{activeSection.title}</h3>
                {activeSection.blocks.map((block, i) => renderBlock(block, i))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
